import { useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useInterlinkRules, type InterlinkRule } from "@/hooks/useInterlinkRules";
import { LinkPreviewCard } from "./LinkPreviewCard";
import { supabase } from "@/integrations/supabase/client";

interface SpiderwebLinkProps {
  text: string;
  /** Maximum total links to inject in this block */
  maxLinks?: number;
}

/**
 * Scans `text` for keyword matches against active interlink rules and renders
 * dofollow <a> tags (via React Router Link) with hover preview cards.
 * SEO-safe: renders real HTML anchor tags with no rel="nofollow".
 */
export const SpiderwebLink = ({ text, maxLinks = 4 }: SpiderwebLinkProps) => {
  const { data: rules } = useInterlinkRules();
  const location = useLocation();
  const currentPath = location.pathname;

  // Log placement (fire-and-forget, non-blocking)
  const logPlacement = useCallback((targetUrl: string, keyword: string) => {
    supabase
      .from("interlink_log")
      .insert({ source_url: currentPath, target_url: targetUrl, keyword_used: keyword })
      .then(() => {});
  }, [currentPath]);

  const segments = useMemo(() => {
    if (!rules || rules.length === 0 || !text) return [{ type: "text" as const, value: text }];

    // Filter out self-links, build sorted candidate list
    const candidates = rules
      .filter((r) => r.target_url !== currentPath)
      .sort((a, b) => a.total_incoming - b.total_incoming);

    if (candidates.length === 0) return [{ type: "text" as const, value: text }];

    type Segment = { type: "text"; value: string } | { type: "link"; rule: InterlinkRule; matchedText: string };
    const result: Segment[] = [];
    let remaining = text;
    let linksInjected = 0;
    const usageCount: Record<string, number> = {};

    // Build a regex from all candidate keywords, longest first to avoid partial matches
    const sortedByLength = [...candidates].sort((a, b) => b.keyword.length - a.keyword.length);

    while (remaining && linksInjected < maxLinks) {
      let earliestMatch: { index: number; length: number; rule: InterlinkRule } | null = null;

      for (const rule of sortedByLength) {
        const used = usageCount[rule.keyword] || 0;
        if (used >= rule.max_uses_per_page) continue;

        const regex = new RegExp(`\\b(${escapeRegex(rule.keyword)})\\b`, "i");
        const match = regex.exec(remaining);
        if (match && (!earliestMatch || match.index < earliestMatch.index)) {
          earliestMatch = { index: match.index, length: match[0].length, rule };
        }
      }

      if (!earliestMatch) {
        result.push({ type: "text", value: remaining });
        break;
      }

      // Text before match
      if (earliestMatch.index > 0) {
        result.push({ type: "text", value: remaining.slice(0, earliestMatch.index) });
      }

      const matchedText = remaining.slice(earliestMatch.index, earliestMatch.index + earliestMatch.length);
      result.push({ type: "link", rule: earliestMatch.rule, matchedText });

      usageCount[earliestMatch.rule.keyword] = (usageCount[earliestMatch.rule.keyword] || 0) + 1;
      linksInjected++;
      remaining = remaining.slice(earliestMatch.index + earliestMatch.length);
    }

    if (remaining && !result.some((s) => s.type === "text" && s.value === remaining)) {
      result.push({ type: "text", value: remaining });
    }

    return result;
  }, [rules, text, currentPath, maxLinks]);

  if (!text) return null;

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === "text") return <span key={i}>{seg.value}</span>;
        // Fire log on first render (mount)
        logPlacement(seg.rule.target_url, seg.rule.keyword);
        return (
          <LinkPreviewCard
            key={i}
            keyword={seg.rule.keyword}
            targetUrl={seg.rule.target_url}
            targetTitle={seg.rule.target_title}
            targetImage={seg.rule.target_image}
            targetType={seg.rule.target_type}
          >
            {seg.matchedText}
          </LinkPreviewCard>
        );
      })}
    </>
  );
};

function escapeRegex(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
